import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CaretUp, CaretDown, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { RevenueChart, ExpiringSoon, DiscountReport, downloadRevenueCsv } from "@/pages/admin/RevenueExtras";

const money = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["en-GB"], { type: "region" }) : null;
const countryName = (code) => { try { return regionNames?.of(code) || code; } catch { return code; } };
const STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800",
  abandoned: "bg-slate-200 text-slate-700", failed: "bg-red-100 text-red-700",
};
const COLS = [
  ["name", "Name"], ["email", "Email"], ["plan", "Plan"], ["start_date", "Start"], ["end_date", "Ends / renews"],
  ["amount", "Amount"], ["status", "Status"], ["country", "Location"],
];

const Totals = ({ t }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="revenue-totals">
    {[["month", `This month · ${t.month_label}`], ["year", `This year · ${t.year_label}`], ["all_time", "All time"]].map(([k, label]) => (
      <div key={k} className="card-recipe !p-5">
        <p className="overline">{label}</p>
        <p className="font-display text-3xl font-black text-rf-ink mt-1" data-testid={`revenue-total-${k}`}>{money(t[k])}</p>
      </div>
    ))}
  </div>
);

const Row = ({ r }) => (
  <tr className="border-t border-rf-ink/10" data-testid={`revenue-row-${r.session_id}`}>
    <td className="p-3 font-semibold">{r.name || "—"}</td>
    <td className="p-3 font-mono text-xs">{r.email}</td>
    <td className="p-3">{r.plan}{r.discount_code && <span className="block text-[10px] text-rf-orange font-bold">{r.discount_code}</span>}</td>
    <td className="p-3 text-xs">{date(r.start_date)}</td>
    <td className="p-3 text-xs">{r.kind === "gift" ? <span className="text-rf-ink-soft">On redemption</span> : date(r.end_date)}</td>
    <td className="p-3 font-semibold">{money(r.amount)}</td>
    <td className="p-3"><span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span></td>
    <td className="p-3 text-xs">{r.country ? countryName(r.country) : r.country === "" ? <span className="text-rf-ink-soft">Unknown</span> : (r.status === "paid" ? <span className="text-rf-ink-soft italic">looking up…</span> : "—")}</td>
  </tr>
);

export default function RevenuePanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showOthers, setShowOthers] = useState(false);
  const [sort, setSort] = useState({ key: "start_date", dir: -1 });

  const load = () => api.get("/admin/revenue").then((r) => setData(r.data)).catch(() => toast.error("Could not load revenue"));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    return data.rows
      .filter((r) => (showOthers ? r.status !== "paid" : r.status === "paid"))
      .filter((r) => !t || (r.email || "").toLowerCase().includes(t) || (r.name || "").toLowerCase().includes(t))
      .sort((a, b) => {
        const x = a[sort.key] ?? "", y = b[sort.key] ?? "";
        return (typeof x === "number" ? x - y : String(x).localeCompare(String(y))) * sort.dir;
      });
  }, [data, q, showOthers, sort]);

  if (!data) return <p className="text-rf-ink-soft">Loading…</p>;
  const otherCount = data.rows.filter((r) => r.status !== "paid").length;

  return (
    <div data-testid="admin-revenue-panel">
      <p className="overline mb-2">Money</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Subscriptions &amp; Revenue</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">
        Every Stripe payment. Totals count completed payments only — plans bought for yourself and gift purchases. Redeemed gifts and free plans are never included. Refreshes every minute.
      </p>
      <Totals t={data.totals} />
      <RevenueChart series={data.monthly || []} planTotals={data.plan_totals} />
      <DiscountReport rows={data.discount_codes || []} />
      <ExpiringSoon rows={data.expiring || []} />

      <div className="card-recipe !p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" size={18} />
          <input data-testid="revenue-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="input-rf !pl-10" />
        </div>
        <div className="inline-flex rounded-full bg-rf-cream-alt p-1" role="tablist">
          <button data-testid="revenue-toggle-paid" onClick={() => setShowOthers(false)} className={`px-4 py-1.5 rounded-full text-sm font-bold ${!showOthers ? "bg-white shadow text-rf-ink" : "text-rf-ink-soft"}`}>
            Completed ({data.totals.paid_count})
          </button>
          <button data-testid="revenue-toggle-others" onClick={() => setShowOthers(true)} className={`px-4 py-1.5 rounded-full text-sm font-bold ${showOthers ? "bg-white shadow text-rf-ink" : "text-rf-ink-soft"}`}>
            Abandoned &amp; failed ({otherCount})
          </button>
        </div>
        <button data-testid="revenue-download" onClick={() => downloadRevenueCsv(rows, showOthers ? "abandoned-failed" : "payments")} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-1"><DownloadSimple size={14} /> Download CSV</button>
        <button data-testid="revenue-refresh" onClick={load} className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-1"><ArrowsClockwise size={14} /> Refresh</button>
      </div>

      <div className="card-recipe !p-0 overflow-x-auto">
        <table className="w-full min-w-[1000px] text-sm" data-testid="revenue-table">
          <thead className="bg-rf-cream-alt">
            <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
              {COLS.map(([k, label]) => (
                <th key={k} className="p-3">
                  <button data-testid={`revenue-sort-${k}`} onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))} className="inline-flex items-center gap-1 uppercase">
                    {label}{sort.key === k && (sort.dir === 1 ? <CaretUp size={10} /> : <CaretDown size={10} />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-rf-ink-soft italic" data-testid="revenue-empty">No payments to show.</td></tr>}
            {rows.map((r) => <Row key={r.session_id} r={r} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
